import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Globe } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { isLocale, persistLocale, SUPPORTED_LOCALES, type Locale } from '@/client/i18n';

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLocale: Locale = isLocale(params.locale) ? params.locale : (i18n.language as Locale);

  useEffect(() => {
    if (!open) return;
    function onClick(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function selectLocale(next: Locale) {
    setOpen(false);
    if (next === currentLocale) return;
    persistLocale(next);
    const segments = location.pathname.split('/');
    if (isLocale(segments[1])) {
      segments[1] = next;
    } else {
      segments.splice(1, 0, next);
    }
    const nextPath = segments.join('/') || `/${next}`;
    navigate(nextPath + location.search + location.hash);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong px-3 h-9 text-body-small font-sans text-text-primary hover:bg-white hover:border-primary/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <Globe size={16} aria-hidden />
        <span className="uppercase tracking-wide">{currentLocale}</span>
        <ChevronDown size={14} aria-hidden className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 min-w-[160px] rounded-md bg-white/95 backdrop-blur-xl border border-glass-border-strong shadow-glass-hover py-1 z-50 animate-fade-up"
        >
          {SUPPORTED_LOCALES.map((locale) => (
            <li key={locale}>
              <button
                type="button"
                role="option"
                aria-selected={locale === currentLocale}
                onClick={() => selectLocale(locale)}
                className={cn(
                  'w-full text-left px-3 py-2 text-body-small font-sans hover:bg-primary-50 transition-colors',
                  locale === currentLocale && 'text-primary font-medium',
                )}
              >
                {t(`language.${locale}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
